// ── Home-screen widget colour ────────────────────────────────────────────────
// One setting (Settings → Widget color) colours every widget. "theme" follows
// the app's own theme/accent (readWidgetTheme in widgetSync.js); "white" is a
// white card with the app's accent, plus an accent border round the Planner's
// day panel so it still stands out; any other value is one of the category
// colours, from which a light pastel background and a vibrant accent (buttons,
// lines, checkboxes) are derived — Jade's spec: pastel and light for the
// background, vibrant for the accent.
//
// Its localStorage key deliberately lacks the `clarity_` prefix: sync.js mirrors
// those to other devices, and widgets exist only on the phone they're on.
import { Capacitor } from '@capacitor/core';


export const widgetColorIsNative = Capacitor.isNativePlatform();

// The Settings row is Android-only (there are no widgets on the web). Flip to
// true to review the design in a browser.
export const SHOW_WIDGET_COLOR_SETTING_ON_WEB_PREVIEW = false;
export const showWidgetColorSetting = widgetColorIsNative || SHOW_WIDGET_COLOR_SETTING_ON_WEB_PREVIEW;

export const WIDGET_COLOR_THEME = 'theme';
export const WIDGET_COLOR_WHITE = 'white';
// Black (Jade, 2026-10-18): a dark card with light text and a vibrant accent.
export const WIDGET_COLOR_BLACK = 'black';
// Picked from the category colour picker's presets (PICKER_PRESETS) — Jade
// wanted the same familiar set, but only colours clearly distinct from each
// other: one per hue, so the near-duplicates (the three blues, the two pinks,
// the two mints) are left out.
export const WIDGET_COLOR_OPTIONS = [
  '#f9c9d6', // pink
  '#fbcabb', // coral
  '#ffd9bb', // peach
  '#fbf1b5', // yellow
  '#e6f0b0', // lime
  '#c4e9bd', // green
  '#bbe9e6', // aqua
  '#b8d0f0', // blue
  '#d4c8f3', // lavender
  '#f0cbef', // orchid
];

const LS_KEY = 'clarityWidgetColor';
export const WIDGET_COLOR_EVENT = 'clarity-widget-color';

const clean = (v) => (v === WIDGET_COLOR_WHITE || v === WIDGET_COLOR_BLACK || WIDGET_COLOR_OPTIONS.includes(v) ? v : WIDGET_COLOR_THEME);

export function loadWidgetColor() {
  try { return clean(localStorage.getItem(LS_KEY)); } catch { return WIDGET_COLOR_THEME; }
}

export function saveWidgetColor(value) {
  const v = clean(value);
  try { localStorage.setItem(LS_KEY, v); } catch { /* private mode etc. */ }
  try { window.dispatchEvent(new Event(WIDGET_COLOR_EVENT)); } catch { /* no window */ }
  return v;
}

// ── Colour maths (HSL) ───────────────────────────────────────────────────────

function hexToHsl(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

function hslToHex(h, s, l) {
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const hex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${hex(f(0))}${hex(f(8))}${hex(f(4))}`;
}

// WCAG contrast of a colour against white.
function contrastWithWhite(hex) {
  const n = parseInt(hex.slice(1), 16);
  const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return 1.05 / (L + 0.05);
}

/**
 * A vibrant version of a (usually pastel) colour, dark enough that white on it
 * stays readable — the widgets' accent, and the Habits widget's bars and
 * checkboxes, which read as washed-out in each habit's own pastel.
 */
export function vibrantAccent(hex) {
  const { h, s } = hexToHsl(hex);
  // Greens, teals and yellows have to go darker than reds and blues for that.
  const accentS = Math.min(0.85, Math.max(0.6, s + 0.15));
  let accentL = 0.52;
  while (accentL > 0.3 && contrastWithWhite(hslToHex(h, accentS, accentL)) < 3) accentL -= 0.01;
  return hslToHex(h, accentS, accentL);
}

/**
 * The widget colours for one category colour: a light pastel card (bg), an
 * almost-white panel, a vibrant accent dark enough for white text on it, and
 * text tinted toward the same hue so nothing reads as a clash.
 */
export function pastelWidgetTheme(hex) {
  const { h, s } = hexToHsl(hex);
  return {
    bg: hslToHex(h, Math.min(0.75, s), 0.92),
    panel: hslToHex(h, Math.min(0.7, s), 0.975),
    text: hslToHex(h, 0.25, 0.2),
    textSoft: hslToHex(h, 0.18, 0.45),
    accent: vibrantAccent(hex),
    onAccent: '#ffffff',
    divider: hslToHex(h, Math.min(0.6, s), 0.88),
  };
}

/**
 * The colours to send to the widgets for a Widget color choice. `readAppTheme`
 * reads the app's live theme (readWidgetTheme) — needed by "theme" and "white".
 */
export function widgetThemeFor(choice, readAppTheme) {
  if (choice === WIDGET_COLOR_WHITE) {
    const app = readAppTheme() || {};
    return {
      ...app,
      bg: '#ffffff',
      panel: '#ffffff',
      // White on white would hide the day panel, so it gets an accent border.
      panelBorder: app.accent || '#d4789a',
    };
  }
  if (choice === WIDGET_COLOR_BLACK) {
    const app = readAppTheme() || {};
    // The app's accent made vibrant (white ticks / + stay readable on it). The
    // card and panel are two steps of near-black, so the Planner's day panel
    // still stands out from the card the way it does on the light colours.
    return {
      bg: '#1c1c1e',
      panel: '#2c2c2e',
      text: '#f2f2f7',
      textSoft: '#a1a1aa',
      accent: vibrantAccent(app.accent || '#d4789a'),
      onAccent: '#ffffff',
      divider: '#3a3a3c',
    };
  }
  if (choice === WIDGET_COLOR_THEME) return readAppTheme();
  return pastelWidgetTheme(choice);
}
